import axios from 'axios';
import { basename } from '../../globals_VITE';

/**
 * Створення, редагування, видалення origin (камери або відеореєстратора)
 * DB Schema:
 * id serial (PK)
 * point_id (FK <point>)
 * origin	text (AK) -- Unique textual identifier, assumed to be auto-generated by backend/DB
 * name text (user friendly name)
 * origin_type_id	int4		not null (FK <origin_type>)
 * credentials	jsonb		 (camera credentials)
 * is_enabled	bool		not null
 */

// Interface for an Origin (camera or NVR)
export interface Origin {
  id: number;           // Primary key (serial)
  point_id: number;     // FK to pcnt.point
  origin: string;       // Unique textual identifier (AK), auto-generated
  name: string;         // User-friendly name
  origin_type_id: number; // FK to pcnt.origin_type
  credentials?: object | null;  // Camera/NVR credentials (JSONB), optional
  is_enabled: boolean;  // Is this origin active?
  poling_period_s?: number; // Polling period in seconds, optional
  success: boolean | null;
  deleted: boolean;
}

// Interface for an Origin linked to a customer, from v_customer_origin
export interface CustomerOrigin extends Origin {
  customer_id: number;
  ref_id?: string | number | null; // ref_id from origin table
}

const API_URL = '/pcnt/v_customer_origin/'; 

const getOrigins = async (point_id: number): Promise<Origin[]> => {
  console.log(`[originApi] getOrigins called for point_id: ${point_id}`);
  const res = await axios.get<Origin[]>(API_URL + `?point_id=${point_id}`);
  return res.data;
}

const getAllOrigins = async (): Promise<Origin[]> => {
  console.log(`[originApi] getOrigins called`);
  const res = await axios.get<Origin[]>(API_URL);
  return res.data;
}

const getCustomerOrigins = async (): Promise<CustomerOrigin[]> => {
  console.log('[originApi] getCustomerOrigins called');
  const res = await axios.get<CustomerOrigin[]>('pcnt/v_customer_origin/');
  return res.data;
}

/**
 * Data required to create an origin.
 * 'id' and 'origin' (textual identifier) are auto-generated.
 */
export type CreateOriginData = Pick<Origin, 'point_id' | 'name' | 'origin'| 'origin_type_id' | 'is_enabled' | 'poling_period_s'> & Partial<Pick<Origin, 'credentials'>>;

const createOrigin = async (data: CreateOriginData): Promise<Origin> => {
  console.log('[originApi] createOrigin called with data:', data);
  const res = await axios.post<Origin>('pcnt/origin/', {...data, face_width_px: 40});
  return res.data;
};

const createOrigin2 = async (data: CreateOriginData): Promise<Origin> => {
  console.log('[originApi] createOrigin called with data:', data);

  try {
    const res = await axios.post<Origin>('pcnt/origin/', { ...data, face_width_px: 40 });
    return res.data;
  } catch (error: any) {
    // Проверяем, что это ошибка Axios и есть ответ от сервера
    if (error.response && error.response.status === 500) {
      const errorData = error.response.data;

      // Если сервер вернул HTML, ищем строку ошибки
      if (typeof errorData === 'string' && errorData.includes('Too much enabled origins')) {
        throw new Error('Превышен лимит активных источников (Database Limit)');
      }
    }

    // Если это другая ошибка, пробрасываем её дальше
    console.error('[originApi] Error creating origin:', error);
    throw error;
  }
};

/**
 * Data required to update an origin. All fields are optional.
 * 'id' and 'origin' (textual identifier) cannot be changed via this method.
 */
export type UpdateOriginData = Partial<Omit<Origin, 'id' >>;

const updateOrigin = async (id: number, data: UpdateOriginData): Promise<Origin> => {
  console.log('[originApi] updateOrigin called for id:', id, 'with data:', data);
  const res = await axios.patch<Origin>(`pcnt/origin/${id}/`, data);
  return res.data;
}

const updateOrigin2 = async (id: number, data: UpdateOriginData): Promise<Origin> => {
  console.log('[originApi] updateOrigin called for id:', id, 'with data:', data);
  try {
  const res = await axios.patch<Origin>(`pcnt/origin/${id}/`, data);
  return res.data;
  } catch (error: any) {    if (error.response && error.response.status === 500) {
      const errorData = error.response.data;
      if (typeof errorData === 'string' && errorData.includes('Too much enabled origins')) {
        throw new Error('Превышен лимит активных источников (Database Limit)');
      }
    }
    console.error('[originApi] Error updating origin:', error);
    throw error;
  }
}

const deleteOrigin = async (id: number): Promise<boolean> => {
  console.log('[originApi] deleteOrigin called for id:', id);
  // const res = await axios.delete(`origin/${id}/`);
  const res = await axios.patch(`pcnt/origin/${id}/`, {deleted: true, is_enabled: false});
  console.log(`[originApi] deleteOrigin response for id ${id}:`, res.data);
  return true;
}

interface OriginCredentials {
  origin_type_id: number;
  credentials: Object;
}

const checkConnection = async (data: OriginCredentials): Promise<{ok: boolean, success: boolean | null, description: string | null}> => {
  console.log('[originApi] checkConnection called :', data);
  try {
    const res = await axios.post(basename + '/pcnt/check_connection/', data);
    return {...res.data, ok: true};
  } catch (err) {
    console.error('[originApi] Error during checkConnection:', err);
    return {ok: false, success: null, description: null};
  }
};

export const originApi = {
  getOrigins,
  getAllOrigins,
  createOrigin,
  createOrigin2,
  updateOrigin,
  updateOrigin2,
  deleteOrigin,
  getCustomerOrigins,
  checkConnection,
};